import type { ReactElement } from 'react';
import type { Route } from './+types/_app.content.$contentAssetId';
import ContentAsset from '@/components/content-asset';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getLogger } from '@/lib/logger.server';
import { fetchRenderableContentAsset, type ContentAssetSeoFields } from '@/lib/api/content-library.server';
import { throwLoaderResponseFromError, throwNotFoundResponse } from '@/lib/api/loader-response.server';

export type ContentAssetPageData = {
    seo: ContentAssetSeoFields;
    body: string;
    contentAssetId: string;
    pageUrl: string;
};

/** Prefer content SEO fields, then display name, then the URL content asset ID. */
export function resolveContentAssetSeoTitle(seo: ContentAssetSeoFields, contentAssetId: string): string {
    return seo.pageTitle?.trim() || seo.name?.trim() || contentAssetId;
}

export function resolveContentAssetSeoDescription(seo: ContentAssetSeoFields): string | undefined {
    const description = seo.pageDescription?.trim() || seo.description?.trim();
    return description || undefined;
}

export async function loader(args: Route.LoaderArgs): Promise<ContentAssetPageData> {
    const logger = getLogger(args.context);
    const { contentAssetId } = args.params;

    if (!contentAssetId) {
        throwNotFoundResponse();
    }

    logger.debug('ContentAssetPage: loader starting', { contentAssetId });

    const requestUrl = new URL(args.request.url);

    try {
        const asset = await fetchRenderableContentAsset(args.context, contentAssetId);

        return {
            seo: asset.seo,
            body: asset.body,
            contentAssetId,
            pageUrl: buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search),
        };
    } catch (error) {
        throwLoaderResponseFromError(error, {
            logger,
            logMessage: 'ContentAssetPage: loader failed',
            logContext: { contentAssetId },
        });
    }
}

/**
 * Standalone content asset page at /:siteId/:localeId/content/:contentAssetId.
 * The URL contentAssetId is the Business Manager content asset library ID.
 */
export default function ContentAssetPage({ loaderData }: { loaderData: ContentAssetPageData }): ReactElement {
    const { seo, body, contentAssetId, pageUrl } = loaderData;
    const seoTitle = resolveContentAssetSeoTitle(seo, contentAssetId);
    const seoDescription = resolveContentAssetSeoDescription(seo);

    return (
        <div className="section-container w-full py-8 md:py-12" data-testid="content-asset-page">
            <SeoMeta
                title={seoTitle}
                description={seoDescription}
                noIndex
                noFollow
                openGraph={{ type: 'article', url: pageUrl }}
            />
            <ContentAsset body={body} />
        </div>
    );
}
